import {
  Alert,
  type AlertProps,
  Box,
  Collapse,
  Stack,
  Text,
} from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { type Control, get, useFormState } from 'react-hook-form';
import { FORM_ERROR } from './types';

// typescript
/**
 * Znajdź najbliższego rodzica posiadającego scroll.
 * Zwraca HTMLElement lub window jeśli brak elementu przewijalnego.
 *
 * @param el - element startowy
 * @param axis - 'y' | 'x' | 'both' - osie do sprawdzenia (domyślnie 'y')
 */
export function findScrollParent(
  el: HTMLElement | null,
  axis: 'y' | 'x' | 'both' = 'y',
): HTMLElement | Window | null {
  if (!el) return null;

  const isScrollable = (node: HTMLElement) => {
    const style = getComputedStyle(node);
    const { overflowY } = style;
    const { overflowX } = style;
    const canScrollY =
      (overflowY === 'auto' ||
        overflowY === 'scroll' ||
        overflowY === 'overlay') &&
      node.scrollHeight > node.clientHeight;
    const canScrollX =
      (overflowX === 'auto' ||
        overflowX === 'scroll' ||
        overflowX === 'overlay') &&
      node.scrollWidth > node.clientWidth;

    if (axis === 'y') return canScrollY;
    if (axis === 'x') return canScrollX;
    return canScrollY || canScrollX;
  };

  let node: HTMLElement | null = el;
  while (node) {
    // skip text nodes etc., tylko elementy
    if (node instanceof HTMLElement && isScrollable(node)) return node;
    node = node.parentElement;
  }

  // fallback na dokument / window (przydatne poza modalami i w body)
  const scrollingEl = document.scrollingElement || document.documentElement;
  if (scrollingEl && isScrollable(scrollingEl as HTMLElement))
    return scrollingEl as HTMLElement;
  return window;
}

function scrollParentToChild(
  parent: HTMLElement | Window | null,
  child: HTMLElement,
  axis: 'y' | 'x' | 'both' = 'y',
) {
  if (!parent || !child) return;
  const behavior: ScrollBehavior = 'smooth';

  // @ts-expect-error
  if (parent instanceof Window || parent === window) {
    const childRect = child.getBoundingClientRect();
    const targetY =
      window.scrollY +
      childRect.top -
      window.innerHeight / 2 +
      childRect.height / 2;
    const targetX =
      window.scrollX +
      childRect.left -
      window.innerWidth / 2 +
      childRect.width / 2;

    if (axis === 'y') window.scrollTo({ top: Math.max(0, targetY), behavior });
    else if (axis === 'x')
      window.scrollTo({ left: Math.max(0, targetX), behavior });
    else
      window.scrollTo({
        top: Math.max(0, targetY),
        left: Math.max(0, targetX),
        behavior,
      });
    return;
  }

  // parent jest HTMLElement (np. modal, container)
  const el = parent as HTMLElement;
  const parentRect = el.getBoundingClientRect();
  const childRect = child.getBoundingClientRect();

  // pozycja dziecka względem scrollTop/scrollLeft rodzica
  const offsetTop = childRect.top - parentRect.top + el.scrollTop;
  const offsetLeft = childRect.left - parentRect.left + el.scrollLeft;

  const targetTop = offsetTop - el.clientHeight / 2 + childRect.height / 2;
  const targetLeft = offsetLeft - el.clientWidth / 2 + childRect.width / 2;

  if (axis === 'y') el.scrollTo({ top: Math.max(0, targetTop), behavior });
  else if (axis === 'x')
    el.scrollTo({ left: Math.max(0, targetLeft), behavior });
  else
    el.scrollTo({
      top: Math.max(0, targetTop),
      left: Math.max(0, targetLeft),
      behavior,
    });
}

/**
 * FormErrorProps
 * @public
 */
export type FormErrorProps = Omit<AlertProps, 'children'> & {
  control?: Control<any, any>;
  name?: string;
};

/**
 * FormError component to display general form error or error for specific field.
 * This component must be used inside Form component (context from react-hook-forms).
 *
 * @example
 * ```tsx
 * <Form onSubmit={onSubmit}>
 *   <FormError />
 *   <TextField name="name" />
 *   <FormButton type="submit">Submit</Button>
 * </Form>
 * ```
 *
 * @public
 * @param control - React Hook Form control
 * @param name - Name of the field to display error for. When not provided, will display general form error
 * @param alertProps - Other props passed to Alert component
 *
 * @see Form
 */
export const FormError: React.FC<FormErrorProps> = ({
  control,
  name = FORM_ERROR,
  ...alertProps
}) => {
  const ref = useRef<any>(undefined);
  const [open, setOpen] = useState(false);

  const state = useFormState({
    control,
  });

  const error = get(state.errors, name);

  useEffect(() => {
    if (error) {
      setOpen(true);
      if (ref?.current) {
        const parent = findScrollParent(ref.current);
        // przewiń rodzica tak, aby ref.current znalazł się na środku
        setTimeout(() => scrollParentToChild(parent, ref.current), 100);
      }
    } else {
      setOpen(false);
    }
  }, [error]);

  const errors = Array.isArray(error) ? error : [error];

  return (
    <Collapse ref={ref} in={open}>
      <Alert
        color="red"
        sx={theme => ({
          padding: theme.other.spacing(1.5, 3, 1.5, 1.5),
        })}
        withCloseButton
        onClose={() => setOpen(false)}
        {...alertProps}
      >
        <Stack>
          {errors.map((error, index) => (
            <Box key={JSON.stringify(error?.message || error || index)}>
              <Text fz="md" color="red">
                {typeof error === 'string' ? error : error?.message}
              </Text>
            </Box>
          ))}
        </Stack>
      </Alert>
    </Collapse>
  );
};
